import { Component, Input, OnInit, ViewEncapsulation, inject, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl, Validators, FormsModule } from '@angular/forms';
import { GbrVacationInfos } from '@coin/shared/gbr/util';
import { Fn } from '@coin/shared/core/util-models';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatFormField, MatSuffix } from '@angular/material/input';
import {
  MatDateRangeInput,
  MatStartDate,
  MatEndDate,
  MatDatepickerToggle,
  MatDateRangePicker
} from '@angular/material/datepicker';
import {
  V2CardComponent,
  V2TextInputComponent,
  V2DropdownComponent,
  V2CheckboxComponent,
  V2DividerComponent
} from '@coin/shared/core/ui-storybook';
import { TemplateWithValueDirective } from '@coin/shared/core/util-directives';
import { SortAlphabeticallyPipe } from '@coin/shared/core/util-pipes';

type Period = GbrVacationInfos['period'];
export enum SimpleGbrInputType {
  Text,
  Dropdown,
  DateRange,
  YesNo
}

/* eslint-disable @typescript-eslint/no-explicit-any */
@Component({
  selector: 'coin-simple-gbr-input',
  templateUrl: './simple-gbr-input.component.html',
  styleUrls: ['./simple-gbr-input.component.scss', '../../styles/gbr-data-point.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    V2CardComponent,
    MatIcon,
    MatTooltip,
    V2TextInputComponent,
    FormsModule,
    V2DropdownComponent,
    TemplateWithValueDirective,
    MatFormField,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatDatepickerToggle,
    MatSuffix,
    MatDateRangePicker,
    V2CheckboxComponent,
    V2DividerComponent,
    TranslatePipe,
    SortAlphabeticallyPipe
  ]
})
export class SimpleGbrInputComponent implements OnInit, ControlValueAccessor {
  control = inject(NgControl, { self: true, optional: true })!;
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);

  @Input() display = false;
  @Input() labelTooltip: string;
  @Input() type: SimpleGbrInputType = SimpleGbrInputType.Text;
  @Input() isNumber: boolean;
  @Input() options: string[] = [];
  @Input() minDate: moment.Moment;
  @Input() maxDate: moment.Moment;

  @Input() label: string;
  @Input() infoText: string;

  public types = SimpleGbrInputType;

  public disabled = false;
  public isTextDisabled = true;

  private _value: any;
  set value(val: any) {
    this._value = val;
    this.onChange(val);
  }
  get value() {
    return this._value;
  }

  get invalid(): boolean {
    if (this.type === SimpleGbrInputType.DateRange) {
      const valueAsPeriod = this.value as Period;
      return !valueAsPeriod.start || !valueAsPeriod.end || this.isDateRangeTooShort();
    }

    return this.control?.invalid;
  }

  get showError(): boolean {
    if (!this.control) {
      return false;
    }

    const { dirty, touched } = this.control;

    return this.invalid && (dirty || touched);
  }

  onChange: Fn = () => {};
  onTouch: Fn = () => {};

  protected get isRequired(): boolean {
    const control = this.control?.control;
    if (!control) {
      return false;
    }
    return control.hasValidator?.(Validators.required) || !!control.validator?.({} as AbstractControl)?.['required'];
  }

  constructor() {
    this.control && (this.control.valueAccessor = this);
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  ngOnInit() {
    if (this.type === SimpleGbrInputType.DateRange && !this.value) {
      this.writeValue({
        start: '',
        end: ''
      });
    }
  }

  private isDateRangeTooShort(): boolean {
    const valueAsPeriod = this.value as Period;
    return valueAsPeriod.start && valueAsPeriod.end && valueAsPeriod.end.diff(valueAsPeriod.start, 'days') < 6;
  }

  getDateRangeText(dateRange: { start: string; end: string }): string {
    if (!dateRange) {
      return '';
    }

    const startText = moment(dateRange.start).format('DD.MM.YYYY');
    const endText = moment(dateRange.end).format('DD.MM.YYYY');
    return `${startText}<br/>${endText}`;
  }

  getRangeValue(dateRange: { start: string; end: string }): number {
    if (!dateRange) {
      return 0;
    }

    return moment(dateRange.end).add(1, 'day').diff(moment(dateRange.start), 'week');
  }

  getGrantValue(dateRange: { start: string; end: string }): number {
    const weeks = this.getRangeValue(dateRange);
    if (weeks >= 2) {
      return 14;
    }
    if (weeks >= 1) {
      return 7;
    }
    return 0;
  }

  toggleYesNo(value: boolean) {
    this.writeValue(value);
    this.onTouch();
  }

  manualChange() {
    this.writeValue(this.value);
    if (this.isDateRangeTooShort()) {
      this.toastrService.error(this.translateService.instant('gbr.period-too-short'));
    }

    this.onTouch();
  }

  userChanges() {
    console.log('User Changes');
    this.onTouch();
  }

  writeValue(value: unknown): void {
    this.value = value;
  }
  registerOnChange(fn: Fn): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: Fn): void {
    this.onTouch = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
